import * as Sentry from '@sentry/vue';
import { assistantWelcome } from '@/domain/assistant/assistant-welcome';
import { activeNavKey } from '@/domain/layout/app-nav';
import type { IAssistantConfigResponse } from '@/interfaces/assistant.interface';
import type {
  IHostAdapter,
  IHostExecuteToolParams,
} from '../gpthelp-host-contract';
import { buildCartadigitalAdminHostContextV2 } from '../cartadigital-admin-host-context';
import { storableText } from '../cards/card-text';
import { getAssistantIdentityToken } from '../gpthelp-identity';
import { HostToolError } from '../tools/tool-types';
import type {
  ICartadigitalAdminHostToolDeps,
  IGpthelpToolTurn,
} from '../tools/tool-types';
import { buildGpthelpToolGate } from '../tools/tool-gate';
import {
  buildCartadigitalAdminHostToolsV1,
  executeCartadigitalAdminTool,
} from '../tools/tool-registry';
import { ensureScopeKeyOrThrow } from '../tools/tool-scope';
import { runAssistantCardAction } from './assistant-card-actions';
import {
  CONFIG_WELCOME_WAIT_MS,
  currentAssistantScopeKey,
  ensureAssistantConfig,
  ensureAssistantConfigWithin,
} from './assistant-config';
import { assistantMenuItems, runAssistantMenuAction } from './assistant-menu';
import { resolveAssistantNavigation } from './assistant-navigate';
import { assistantToolLabels } from './assistant-tool-labels';

/** What the adapter needs from the running admin besides the stores. */
export interface IAssistantHostHooks {
  /** `router.push`; a failed or duplicated navigation is ignored. */
  push(location: { path: string; query: Record<string, string> }): void;
  /** Opens a link outside the admin (the in-app browser in the APK). */
  openExternal(url: string): Promise<void>;
  /** `navigator.onLine`. */
  isOnline(): boolean;
  /** Reloads the admin. */
  reload(): void;
}

/** Rejections a tool request can meet before it runs (plan §9.5). */
const TOOL_REJECTIONS = ['SCOPE_MISMATCH', 'NOT_OFFERED', 'NOT_AVAILABLE'];

/** The user message that started a turn is sent to the tools cut at this length. */
const USER_TEXT_MAX = 500;

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * The widget's turn fields; empty for a widget that does not send them. The
 * user's text is cut by characters (never inside an emoji) and without the
 * half emoji a widget's own cut may leave: it reaches the proposal card and
 * the Action API.
 */
function toolTurn(params: IHostExecuteToolParams): IGpthelpToolTurn {
  return {
    conversationId: text(params.conversationId),
    turnId: text(params.turnId),
    requestId: text(params.requestId),
    tainted: params.tainted === true,
    userText: Array.from(storableText(text(params.userText)))
      .slice(0, USER_TEXT_MAX)
      .join(''),
  };
}

/**
 * The host adapter v2 of the admin (plan §9.5) over its stores and hooks:
 * context, tools and their checks, identity, welcome, labels, the ⋯ menu,
 * card actions and navigation.
 */
export function buildAdminHostAdapter(
  deps: ICartadigitalAdminHostToolDeps,
  hooks: IAssistantHostHooks
): IHostAdapter {
  /** The names `getTools` last offered, and for which store and branch. */
  let offered: { scopeKey: string; names: Set<string> } | null = null;

  const t = (key: string): string => {
    const value = deps.i18n.t(key, deps.i18n.locale, {});
    return typeof value === 'string' ? value : key;
  };

  /** The config of the current store and branch; null while unknown. */
  const currentConfig = (): IAssistantConfigResponse | null => {
    const key = currentAssistantScopeKey(deps);
    return key && deps.assistantStore.configKey === key
      ? deps.assistantStore.config
      : null;
  };

  return {
    getContext() {
      return buildCartadigitalAdminHostContextV2(deps);
    },

    getTools() {
      // Never waits: getTools has one second. The config the welcome loaded
      // is already there; otherwise this send reads without the writes.
      void ensureAssistantConfig(deps);
      const tools = buildCartadigitalAdminHostToolsV1(
        buildGpthelpToolGate(deps)
      );
      const scopeKey = currentAssistantScopeKey(deps);
      offered = scopeKey
        ? { scopeKey, names: new Set(tools.map((tool) => tool.name)) }
        : null;
      return tools;
    },

    async executeTool(params) {
      const name = text(params.name);
      try {
        // 1. The scope the turn was asked in is the current one.
        ensureScopeKeyOrThrow(deps, params.scopeKey);
        // 2. The tool was offered for it (a name outside the set never runs).
        if (
          !offered ||
          offered.scopeKey !== params.scopeKey ||
          !offered.names.has(name)
        ) {
          throw new HostToolError(
            'NOT_OFFERED',
            'This tool was not offered for this request.',
            { name }
          );
        }
        // 3. The registry re-checks the gate with the current stores and runs it.
        return await executeCartadigitalAdminTool({
          name,
          args: params.args,
          deps,
          scopeKey: params.scopeKey,
          turn: toolTurn(params),
        });
      } catch (error) {
        if (
          error instanceof HostToolError &&
          TOOL_REJECTIONS.includes(error.code)
        ) {
          Sentry.addBreadcrumb({
            category: 'assistant',
            message: 'tool_rejected',
            level: 'warning',
            data: { name, code: error.code },
          });
        }
        throw error;
      }
    },

    getIdentityToken() {
      const userId = deps.authStore.user?.id;
      const restaurantId = deps.restaurantStore.restaurant?.id;
      const branchId = deps.restaurantStore.selectedBranch?.id;
      return getAssistantIdentityToken(
        typeof userId === 'number' &&
          typeof restaurantId === 'number' &&
          typeof branchId === 'number'
          ? { userId, restaurantId, branchId, locale: deps.i18n.locale }
          : null
      );
    },

    async getWelcome() {
      // The first open of each store and branch loads what the assistant may
      // change; a slow answer never holds the welcome (read-only meanwhile).
      await ensureAssistantConfigWithin(deps, CONFIG_WELCOME_WAIT_MS);
      const gate = buildGpthelpToolGate(deps);
      if (!gate.hasRestaurantScope) return null;
      const pageKey = activeNavKey(deps.routePath(), deps.restaurantNav());
      return assistantWelcome(pageKey, gate, t);
    },

    getToolLabels() {
      const names = buildCartadigitalAdminHostToolsV1(
        buildGpthelpToolGate(deps)
      ).map((tool) => tool.name);
      return assistantToolLabels(names, t);
    },

    getMenu() {
      void ensureAssistantConfig(deps);
      return assistantMenuItems(buildGpthelpToolGate(deps), currentConfig(), t);
    },

    onMenuAction(id) {
      return runAssistantMenuAction(id, currentConfig(), {
        deps,
        openExternal: hooks.openExternal,
      });
    },

    onCardAction(event) {
      return runAssistantCardAction(event, {
        deps,
        isOnline: hooks.isOnline,
        reload: hooks.reload,
        openExternal: hooks.openExternal,
      });
    },

    navigate(target) {
      const navigation = resolveAssistantNavigation(
        target,
        buildGpthelpToolGate(deps).navTargets
      );
      if (!navigation) {
        console.warn('[assistant] no page to open for', target.page);
        return;
      }
      hooks.push({ path: navigation.path, query: navigation.query });
      if (navigation.highlightItemIds.length)
        deps.assistantStore.requestHighlight(
          navigation.highlightItemIds,
          navigation.categoryId
        );
      // The overlay closes and the sheet goes low, so the page shows.
      deps.assistantStore.revealPage();
    },
  };
}
